import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * ClientModuleGuard — route-level enforcement of the per-client module
 * switches a superadmin sets on the Configuration Management page.
 *
 * A module that is OFF for the client is hidden for every role in it
 * (admin, manager, admin-level, plain user) in every view; only superadmin
 * bypasses. Use it on shells whose routes are not already wrapped in
 * PageGuard (PageGuard's getPageState applies the same switch itself).
 *
 *   <ClientModuleGuard module="attendance" redirectTo="/userprofile">
 *     <AttendancePage />
 *   </ClientModuleGuard>
 */
/**
 * Self-service page name → the per-client module switch that owns it. Shared
 * by every "user view" shell (plain user, admin-as-user, manager-as-user) so
 * their sidebars and routes hide the same pages. Kept apart from the
 * admin-level pageToModuleMaps on purpose: those deliberately leave
 * universal self-service pages (escalation) unmapped, whereas the client
 * switch applies to them too.
 */
export const SELF_SERVICE_PAGE_MODULES = {
  salary: "payroll",
  attendance: "attendance",
  // Self Services hosts BOTH document requests and advancements; the page
  // stays while either is on and hides each half by itself (Documents_Page).
  "employee-services": ["documents", "advancements"],
  calendar: "calendar",
  "leave-management": "leaves",
  messages: "messages",
  escalation: "complaints",
  groups: "groups",
};

/** A page mapped to one module needs it on; mapped to several needs ANY on. */
export const isAnyClientModuleEnabled = (isClientModuleEnabled, modules) => {
  if (!modules) return true;
  const list = Array.isArray(modules) ? modules : [modules];
  return list.length === 0 || list.some((m) => isClientModuleEnabled(m));
};

/** Drop nav items whose module(s) are switched off for the client. */
export const filterNavByClientModules = (navItems, isClientModuleEnabled, map = SELF_SERVICE_PAGE_MODULES) =>
  navItems.filter((item) => isAnyClientModuleEnabled(isClientModuleEnabled, map[item.page]));

const ClientModuleGuard = ({ module, redirectTo = "/", children }) => {
  const { isClientModuleEnabled, clientModules, clientModulesLoading } = useAuth();

  if (!module) return children;
  // Don't mount a page that is about to be redirected away from.
  if (clientModulesLoading && clientModules === null) return null;
  if (!isAnyClientModuleEnabled(isClientModuleEnabled, module)) return <Navigate to={redirectTo} replace />;
  return children;
};

export default ClientModuleGuard;
